/**
 * Stripe Agentic Commerce Service
 *
 * Functions for connecting to Stripe Agentic Commerce.
 * Uses the generic OAuth connector pattern (same as /connections page).
 */

import Config from '../../config';
import axios from 'axios';
import { AgenticField, LabelledOption } from '../../components/DynamicField';

export type { LabelledOption };

/**
 * Connection status enum
 */
export const ConnectionStatus = {
  CONNECTED: 'connected',
  NOT_CONNECTED: 'not_connected',
  PENDING: 'pending',
  ERROR: 'error',
} as const;

export type ConnectionStatusType = (typeof ConnectionStatus)[keyof typeof ConnectionStatus];

/** Alias for backward compatibility */
export type StripeField = AgenticField;

/**
 * A connector action defined by the backend
 */
export interface ConnectorAction {
  Name: string;
  FriendlyName: string;
}

/**
 * Return type for getStripeConnectionStatus
 */
export interface StripeConnectionResult {
  status: ConnectionStatusType;
  settings: Record<string, string | null>;
  fields: StripeField[];
  actions: ConnectorAction[];
  isConnected: boolean;
  oauthName: string | null;
}

/**
 * Shape of a single setting from the backend GetAllSystemSettings response
 */
interface SystemSetting {
  Name: string;
  Value: string | null;
  FriendlyName: string;
  Secret?: boolean;
  Options?: string[];
  LabelledOptions?: LabelledOption[];
  DefaultValue?: string;
  Section?: string;
  IndirectEditable?: boolean;
}

/**
 * Shape of a single system from the backend GetAllSystemSettings response
 */
interface SystemSettingsEntry {
  System: string;
  IsConnected: boolean;
  OAuthName?: string;
  Settings: SystemSetting[];
  Actions?: ConnectorAction[];
}

/**
 * Get Stripe system settings, connection status, and field definitions from the connector settings API.
 * Returns OAuthName for OAuth redirect and fields for dynamic form rendering.
 */
export const getStripeConnectionStatus = async (): Promise<StripeConnectionResult> => {
  try {
    const res = await axios.get<{ value: SystemSettingsEntry[] }>(
      Config.api + '/odata/Company/Functions.GetAllSystemSettings'
    );
    const stripe = res.data.value.find((r) => r.System === 'Stripe');

    if (!stripe) {
      return {
        status: ConnectionStatus.NOT_CONNECTED,
        settings: {},
        fields: [],
        actions: [],
        isConnected: false,
        oauthName: null,
      };
    }

    // Convert settings array to object for form initialValues
    const settingsObj = (stripe.Settings || []).reduce<Record<string, string | null>>((acc, setting) => {
      acc[setting.Name] = setting.Value;
      return acc;
    }, {});

    // Build field definitions for dynamic form rendering (same pattern as connections.js:94-112)
    const fields: StripeField[] = (stripe.Settings || [])
      .filter((r) => !r.IndirectEditable)
      .map((r) => ({
        name: r.Name,
        label: r.FriendlyName,
        secret: r.Secret || false,
        options: r.LabelledOptions || [],
        hasOptions: !!(r.Options && r.Options.length > 0),
        placeholder: r.DefaultValue || null,
        section: r.Section || null,
      }));

    return {
      status: stripe.IsConnected ? ConnectionStatus.CONNECTED : ConnectionStatus.NOT_CONNECTED,
      settings: settingsObj,
      fields,
      actions: stripe.Actions || [],
      isConnected: stripe.IsConnected || false,
      oauthName: stripe.OAuthName || null,
    };
  } catch (error) {
    console.error('Error fetching Stripe connection status:', error);
    return {
      status: ConnectionStatus.ERROR,
      settings: {},
      fields: [],
      actions: [],
      isConnected: false,
      oauthName: null,
    };
  }
};

/**
 * Save Stripe system settings
 */
export const saveStripeSettings = async (settings: Record<string, unknown>): Promise<void> => {
  const payload: Record<string, unknown> = { ...settings, System: 'Stripe' };
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) {
      payload[k] = null;
    }
  });
  await axios.put(Config.api + `/odata/Company/Functions.SetSystemSettings`, payload, { params: { system: 'Stripe' } });
};

/**
 * Initiate Stripe OAuth connection.
 * Saves settings first, then redirects to the generic OAuth flow.
 * This mirrors the "Connect" button behavior on the /connections page (connections.js:125).
 */
export const connectStripe = async (settings: Record<string, unknown>, oauthName: string): Promise<void> => {
  await saveStripeSettings(settings);
  window.location.href = `/oauth/${oauthName}`;
};

/**
 * Execute a connector action for Stripe
 */
export const runStripeAction = async (actionName: string, settings: Record<string, unknown>): Promise<string> => {
  const result = await axios.post<string>(Config.api + `/odata/Company/Functions.ExecuteConnectorAction`, settings, {
    params: { system: 'Stripe', actionName },
  });
  return result.data;
};

/**
 * Disconnect Stripe account from Logicbroker.
 * Uses OAuthName for the disconnect call (same as connections.js:128).
 */
export const disconnectStripe = async (oauthName: string): Promise<void> => {
  await axios.get(Config.api + `/odata/Company/Functions.DisconnectOAuth?system=${oauthName}`);
};
